import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Modal, Form, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import { toast } from "react-toastify";
import { checkPermissionStatus, getDeviceInfo } from "helpers/utils";
import {
  fetchAllResumesHelper,
  removeResumeHelper,
  uploadResumeHelper,
} from "helpers/backend_helper";
import serach_icon from "../../assets/images/icon/search-icon.svg";
import { decryptDataFromArray } from "helpers/EncDec";

import delete_icon from "../../assets/images/icon/delete.svg";
import right_arrow from "../../assets/images/icon/right-arrow-table.svg";
import arrow_btn from "../../assets/images/icon/arrow-btn.svg";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { getUserInfo } from "helpers/authHelper";
import Swal from "sweetalert2";

const ResumeList = () => {
  const { t } = useTranslation();
  const [allResumes, setAllResumes] = useState([]);
  const tableWrapperRef = useRef(null);

  const [addModal, setAddModal] = useState(false);
  const [addResume, setAddResume] = useState(null);

  const toggleAddModal = () => {
    setAddModal(!addModal);
    setAddResume(null);
  };

  const [filters, setFilters] = useState({
    query: "",
  });

  const fetchAllResumes = async () => {
    try {
      let response = await fetchAllResumesHelper({});

      let responseData = decryptDataFromArray(response);

      let allResumes = responseData?.data?.allResumes || [];
      setAllResumes(allResumes);
    } catch (error) {
      toast.error(t(`Resume_List.No resumes found`));
      setAllResumes([]);
    }
  };

  const handleUploadResume = async () => {
    try {
      if (!addResume) {
        throw new Error(t(`Resume_List.No resume error`));
      }

      let userInfo = getUserInfo();
      let adminId = userInfo?.id;

      let device_info = await getDeviceInfo(navigator);

      let formData = new FormData();

      formData.append("resume", addResume);
      formData.append("source", "NeuroTal Admin");
      formData.append("adminId", adminId);
      formData.append("device_info", JSON.stringify(device_info));

      let response = await uploadResumeHelper({
        formData: formData,
      });

      toast.success(t(`Resume_List.Resume uploaded successfully`));
      fetchAllResumes();
      toggleAddModal();
    } catch (error) {
      toast.error(error?.message);
    }
  };

  const handleRemoveResume = async resumeId => {
    try {
      let userInfo = getUserInfo();
      let adminId = userInfo?.id;

      let response = await removeResumeHelper({
        removeAdminId: adminId,
        resumeId: resumeId,
      });

      toast.success(t(`Resume_List.Resume removed successfully`));

      fetchAllResumes();
    } catch (error) {
      let errMsg =
        error?.response?.data?.message ||
        error?.response?.message ||
        error?.message ||
        t(`Resume_List.Remove resume error`);

      toast.error(errMsg);
    }
  };

  useEffect(() => {
    fetchAllResumes();

    return () => {};
  }, []);

  const columns = [
    {
      dataField: "source",
      text: t(`Resume_List.Source`),
    },
    {
      dataField: "uploaded_by.name",
      text: t(`Resume_List.Uploaded By`),
      classes: "white-hidden",
    },
    {
      dataField: "createdAt",
      text: t(`Resume_List.Uploaded At`),
      classes: "white-hidden",
      formatter: (createdAt, resume) =>
        moment(createdAt).format("Do MMM, YYYY"),
    },
    {
      dataField: "resume_url",
      text: t(`Resume_List.URL`),
      classes: "white-hidden",
      formatter: (resume_url, resume) => {
        if (!resume_url) {
          return t(`Resume_List.Resume Not Available`);
        }

        let urlObj = new URL(resume_url);
        let pathname = urlObj.pathname;
        let filename = pathname.substring(pathname.lastIndexOf("/") + 1);

        return (
          <a href={resume_url} download={filename} target="_blank">
            {filename}
          </a>
        );
      },
    },
  ];

  if (checkPermissionStatus("resume-list", "delete")) {
    columns.push({
      dataField: "id",
      text: t(`Resume_List.Actions`),
      formatter: (resumeId, resume) => {
        return (
          <div className="d-flex gap-1 blue-icon-gap action-application">
            <div className="action-one small-img">
              <div
                className="text-success blue-icon blue-color3"
                role="button"
                title="Remove Resume"
                onClick={e => {
                  Swal.fire({
                    title: t("Resume_List.Are you sure"),
                    text: t("Resume_List.You won't be able to revert this"),
                    icon: "warning",
                    showCancelButton: true,
                    cancelButtonColor: "#d33",
                    cancelButtonText: t("Resume_List.Cancel"),
                    confirmButtonColor: "#3085d6",
                    confirmButtonText: t("Resume_List.Remove"),
                    reverseButtons: true,
                  }).then(async result => {
                    if (!result.isConfirmed) {
                      return;
                    }

                    handleRemoveResume(resumeId);
                  });
                }}
              >
                <img src={delete_icon} alt="delete_icon" />
                <span>{t(`Resume_List.Remove`)}</span>
              </div>
            </div>
          </div>
        );
      },
    });
  }

  const customTotal = (from, to, size) => (
    <span className="react-bootstrap-table-pagination-total">
      {t(`Resume_List.Showing rows`, { from, to, size })}
    </span>
  );

  useEffect(() => {
    const theme = localStorage.getItem("company_theme");
    if (theme) {
      document.body.classList.add(`theme-${theme}`);
    }
  }, []);

  const options = {
    paginationSize: 1,
    pageStartIndex: 1,
    alwaysShowAllBtns: true, // Always show next and previous button
    withFirstAndLast: true, // Hide the going to First and Last page button
    hideSizePerPage: true, // Hide the sizePerPage dropdown always
    hidePageListOnlyOnePage: true, // Hide the pagination list when only one page
    firstPageText: false,
    prePageText: false,
    nextPageText: false,
    lastPageText: false,
    nextPageTitle: "Next page",
    prePageTitle: "Preious page",
    firstPageTitle: "First page",
    lastPageTitle: "Last page",
    showTotal: true,
    paginationTotalRenderer: customTotal,
    disablePageTitle: false,
  };

  if (!checkPermissionStatus("resume-list", "view")) {
    return (
      <div className="page-content">
        <h4 className="text-center">{t(`Resume_List.Access Denied`)}</h4>
      </div>
    );
  }

  /**table arrow */
  const handleScroll = direction => {
    if (tableWrapperRef.current) {
      const scrollAmount = 100; // Adjust this value as needed
      tableWrapperRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="page-content departmentpage">
      <Container fluid>
        <div className="joblist-container skilllist  applicationcard-table">
          <Row className="mb-3 align-items-center py-2 spece-low-cum2">
            <Col lg="4" md="4" className="Joblist-column">
              <div className="joblist-container create-vacancy p-0">
                <Link to="/">
                  <h5 className="text-uppercase heading-one spece-low-cum2">
                    <img src={arrow_btn} alt="arrow_btn" />
                    {t(`Resume_List.List`)}
                  </h5>
                </Link>
              </div>
            </Col>
            <Col lg="8" md="8">
              <div className="single-filter-box skillset-card btn-left d-flex justify-content-end align-items-center gap-2 flex-wraps">
                <div className="serach-bar-top">
                  <form className="form-inline">
                    <input
                      className="form-control mr-sm-2"
                      type="search"
                      placeholder={t(`Resume_List.Search`)}
                      aria-label={t(`Resume_List.Search`)}
                      onChange={e => {
                        let text = e.target?.value;

                        setFilters(prevState => ({
                          ...prevState,
                          query: text,
                        }));
                      }}
                      title={t(`Resume_List.Search`)}
                    />
                    <span>
                      <img src={serach_icon} alt="search" />
                    </span>
                  </form>
                </div>

                {checkPermissionStatus("resume-list", "upload") && (
                  <div
                    role="button"
                    className="btn btn-primary"
                    onClick={() => {
                      toggleAddModal();
                    }}
                  >
                    {t(`Resume_List.Upload`)}
                  </div>
                )}

                <div className="tale-text right-panel skilltest">
                  <button
                    className="button left"
                    onClick={() => handleScroll("left")}
                    data-tooltip="Scroll Left"
                  >
                    <img src={right_arrow} alt="right-arrow" />
                  </button>
                  <button
                    className="button right"
                    onClick={() => handleScroll("right")}
                    data-tooltip="Scroll Right"
                  >
                    <img src={right_arrow} alt="right-arrow" />
                  </button>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        <Row>
          <Col lg={12}>
            <div className="outerTab-box-wrapper below-vacancy  skillset-cards-po">
              <div className="timeto-hire-card userlist">
                <div className="tabel-application tabel-application-auto table-responsive white-space-row">
                  <ToolkitProvider
                    keyField="id"
                    data={allResumes?.filter(resume => {
                      let query = filters?.query?.trim();

                      if (!query) {
                        return true;
                      }

                      let source = resume?.source;
                      let adminName = resume?.uploaded_by?.name;
                      let resume_url = resume?.resume_url;

                      let sourceFlag = source
                        ?.toLowerCase()
                        ?.includes(query?.toLowerCase());

                      let adminNameFlag = adminName
                        ?.toLowerCase()
                        ?.includes(query?.toLowerCase());

                      let resumeUrlFlag = resume_url
                        ?.toLowerCase()
                        ?.includes(query?.toLowerCase());

                      return sourceFlag || adminNameFlag || resumeUrlFlag;
                    })}
                    columns={columns}
                    search
                  >
                    {props => (
                      <div className="table-custome-class range-custome">
                        <div className="scroll-container">
                          <div className="table-pending-list">
                            <div
                              className="table-wrapper"
                              ref={tableWrapperRef}
                            >
                              <BootstrapTable
                                {...props.baseProps}
                                pagination={paginationFactory(options)}
                                options={options}
                                striped
                                hover
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </ToolkitProvider>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      <Modal size="lg" show={addModal} onHide={toggleAddModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t(`Resume_List.Upload`)}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-2 floating-label">
            <Form.Label>
              Resume<span className="text-danger">*</span>
            </Form.Label>

            <label className="btn btn-sm btn-primary ms-2">
              Upload Resume
              <Form.Control
                className="d-none"
                type="file"
                accept=".pdf,.docx,.doc"
                onChange={e => {
                  setAddResume(e?.target?.files?.[0]);
                }}
              />
            </label>
          </Form.Group>

          <Form.Group className="d-flex gap-2 justify-content-center mt-4 mb-0">
            <Button type="reset" variant="secondary" onClick={toggleAddModal}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="success"
              onClick={() => {
                handleUploadResume();
              }}
            >
              Upload
            </Button>
          </Form.Group>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ResumeList;
